import { create } from 'zustand';
import { watchlistSelfService, WatchlistSelfItem } from '../services/watchlistSelfService';
import { SortDirection } from '@/services/enums';
import { useToastStore } from './useToastStore';
import { Pagination } from '../types/common';

const STORAGE_KEY = 'watchlist_self_cache';

interface WatchlistSelfState {
  // 状态
  items: WatchlistSelfItem[];
  isLoading: boolean;
  error: string | null;
  selfSelectedSymbols: Set<string>; // 用于快速检查是否已自选
  addingSymbols: Set<string>; // 正在添加的币种（用于显示加载状态）
  removingSymbols: Set<string>; // 正在移除的币种（用于显示加载状态）
  pagination?: Pagination;

  // Actions
  addToWatchlistSelf: (symbol: string, instType: string, coinId?: string) => Promise<void>;
  removeFromWatchlistSelf: (symbol: string, instType: string) => Promise<void>;
  checkIsSelfSelected: (symbol: string, instType: string) => boolean;
  refreshWatchlistSelf: (
    showLoading?: boolean,
    sortBy?: string,
    sortOrder?: SortDirection,
    page?: number,
    pageSize?: number,
    search?: string,
    includeExtendedFields?: boolean,
  ) => Promise<void>;
  loadFromCache: () => boolean;
  saveToCache: () => void; // 保存当前数据到缓存
  clearError: () => void;
}

export const useWatchlistSelfStore = create<WatchlistSelfState>((set, get) => ({
  // 初始状态
  items: [],
  isLoading: false,
  error: null,
  selfSelectedSymbols: new Set(),
  addingSymbols: new Set(),
  removingSymbols: new Set(),

  // 添加到自选
  addToWatchlistSelf: async (symbol: string, instType: string, coinId?: string) => {
    const key = `${symbol}-${instType}`;

    // 设置添加中状态
    set((state) => {
      const newAddingSymbols = new Set(state.addingSymbols);
      newAddingSymbols.add(key);
      return { addingSymbols: newAddingSymbols, error: null };
    });

    try {
      const response = await watchlistSelfService.addToWatchlistSelf(symbol, instType, coinId);
      // 提示
      useToastStore.getState().show('已添加到自选', 'success');

      // 使用后端返回的完整数据（包含 coin_base 数据）
      const newItem: WatchlistSelfItem = {
        id: response?.id || Date.now(),
        symbol,
        instType,
        createdAt: response?.createdAt || new Date().toISOString(),
        key: response?.key,
        // coin_base 字段从后端返回的数据中获取
        coinBaseSymbol: response?.coinBaseSymbol || null,
        imageThumb: response?.imageThumb || null,
        fullyDilutedValuationUsd: response?.fullyDilutedValuationUsd || null,
        totalVolumeUsd: response?.totalVolumeUsd || null,
        totalSupply: response?.totalSupply || null,
        maxSupply: response?.maxSupply || null,
        circulatingSupply: response?.circulatingSupply || null,
      };

      // 手动添加到 items（添加到列表开头，因为后端是按 createdAt desc 排序的）
      set((state) => {
        const newItems = [newItem, ...state.items];
        const newSelfSelectedSymbols = new Set(state.selfSelectedSymbols);
        newSelfSelectedSymbols.add(key);
        return { items: newItems, selfSelectedSymbols: newSelfSelectedSymbols };
      });

      // 清除本地缓存，强制下次从服务器获取最新数据
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.warn('清除缓存失败:', error);
      }
    } catch (error: any) {
      set({ error: error.message || '添加到自选失败' });
      useToastStore.getState().show(error.message || '添加到自选失败', 'error');
      throw error;
    } finally {
      // 清除添加中状态
      set((state) => {
        const newAddingSymbols = new Set(state.addingSymbols);
        newAddingSymbols.delete(key);
        return { addingSymbols: newAddingSymbols };
      });
    }
  },

  // 从自选中移除
  removeFromWatchlistSelf: async (symbol: string, instType: string) => {
    const key = `${symbol}-${instType}`;

    // 设置移除中状态
    set((state) => {
      const newRemovingSymbols = new Set(state.removingSymbols);
      newRemovingSymbols.add(key);
      return { removingSymbols: newRemovingSymbols, error: null };
    });

    try {
      await watchlistSelfService.removeFromWatchlistSelf(symbol, instType);
      useToastStore.getState().show('已从自选中移除', 'success');

      // 手动从 items 中移除
      set((state) => {
        const newItems = state.items.filter((item) => !(item.symbol === symbol && item.instType === instType));
        const newSelfSelectedSymbols = new Set(state.selfSelectedSymbols);
        newSelfSelectedSymbols.delete(key);
        return { items: newItems, selfSelectedSymbols: newSelfSelectedSymbols };
      });

      // 清除本地缓存，强制下次从服务器获取最新数据
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.warn('清除缓存失败:', error);
      }
    } catch (error: any) {
      set({ error: error.message || '移除失败' });
      useToastStore.getState().show(error.message || '移除失败', 'error');
      throw error;
    } finally {
      // 清除移除中状态
      set((state) => {
        const newRemovingSymbols = new Set(state.removingSymbols);
        newRemovingSymbols.delete(key);
        return { removingSymbols: newRemovingSymbols };
      });
    }
  },

  // 检查是否已自选
  checkIsSelfSelected: (symbol: string, instType: string) => {
    const key = `${symbol}-${instType}`;
    return get().selfSelectedSymbols.has(key);
  },

  // 刷新自选列表
  refreshWatchlistSelf: async (
    showLoading: boolean = true,
    sortBy: string = 'default',
    sortOrder: SortDirection = 'desc',
    page?: number,
    pageSize?: number,
    search?: string,
    includeExtendedFields: boolean = false,
  ) => {
    if (showLoading) {
      set({ isLoading: true, error: null });
    }

    try {
      const response = await watchlistSelfService.getWatchlistSelf(
        sortBy,
        sortOrder,
        page,
        pageSize,
        search,
        includeExtendedFields,
      );

      // 如果返回的是分页结构
      if (response && 'data' in response && 'pagination' in response) {
        const { data, pagination } = response;
        const newSelfSelectedSymbols = new Set<string>();
        data.forEach((item: WatchlistSelfItem) => {
          newSelfSelectedSymbols.add(`${item.symbol}-${item.instType}`);
        });

        set({
          items: data,
          pagination,
          selfSelectedSymbols: newSelfSelectedSymbols,
          isLoading: false,
        });
      } else {
        // 返回的是数组
        const items = Array.isArray(response) ? response : (response as any).data || [];
        const newSelfSelectedSymbols = new Set<string>();
        items.forEach((item: WatchlistSelfItem) => {
          newSelfSelectedSymbols.add(`${item.symbol}-${item.instType}`);
        });

        set({
          items,
          pagination: undefined,
          selfSelectedSymbols: newSelfSelectedSymbols,
          isLoading: false,
        });
      }

      // 保存到本地缓存
      get().saveToCache();
    } catch (error: any) {
      set({ error: error.message || '获取自选列表失败', isLoading: false });
      useToastStore.getState().show(error.message || '获取自选列表失败', 'error');
    }
  },

  // 从缓存加载数据
  loadFromCache: () => {
    try {
      const cachedData = localStorage.getItem(STORAGE_KEY);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);

        // 兼容旧格式（直接是数组）
        let items: WatchlistSelfItem[];
        let pagination: Pagination | undefined;

        if (Array.isArray(parsed)) {
          // 旧格式：直接是数组
          items = parsed;
          pagination = undefined;
        } else if (parsed && parsed.items && Array.isArray(parsed.items)) {
          // 新格式：包含 items 和 pagination
          items = parsed.items;
          pagination = parsed.pagination || undefined;
        } else {
          return false;
        }

        const symbols = new Set<string>();
        items.forEach((item) => {
          symbols.add(`${item.symbol}-${item.instType}`);
        });

        set({
          items,
          selfSelectedSymbols: symbols,
          pagination,
          error: null,
        });
        return true;
      }
    } catch (error) {
      console.error('从缓存加载数据失败:', error);
    }
    return false;
  },

  // 保存当前数据到缓存
  saveToCache: () => {
    const state = get();
    try {
      const cacheData = {
        items: state.items,
        pagination: state.pagination || null,
        timestamp: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cacheData));
      console.log(`[WatchlistSelf] 保存缓存成功（${state.items.length}条）`);
    } catch (error) {
      console.warn('保存缓存失败:', error);
    }
  },

  // 清除错误
  clearError: () => {
    set({ error: null });
  },
}));
